import {Typography} from '@mui/material';
import {DlMuiButton, LoadingSpinner, DlMuiDynamicIcon, PopupWrapper} from '@wallaby-ui/core';
import {LightTable, useFormatters} from '@wallaby-ui/document';
import React, {useCallback, useState} from 'react';

import {ShortcutData} from '../DrpCombinationManagementDialog';
import {CombinationInstance} from '../useCombinations';

type Props = {
  instances: CombinationInstance[];
  isLoading: boolean;
  shortcuts: ShortcutData[];
  onEdit: (instance: CombinationInstance) => void;
  onDelete: (instanceId: number) => void;
  onNewCombination: () => void;
};

export const CombinationList: React.FC<Props> = ({instances, isLoading, shortcuts, onEdit, onDelete, onNewCombination}) => {
  const {t, tEnum} = useFormatters();

  const getValuesPreview = useCallback(
    (instance: CombinationInstance) => {
      if (!instance.values || instance.values.length === 0) {
        return '-';
      }

      const dimensionLabels = {
        DRP_TYPE: t('hospitalization.detail.phRec.drp.type'),
        DRP_CAUSE: t('hospitalization.detail.phRec.drp.causes'),
        DRP_INTERVENTION: t('hospitalization.detail.phRec.drp.plannedInterventions'),
      };

      /* eslint-disable no-param-reassign */
      const grouped = instance.values.reduce((acc, value) => {
        const dim = value.key.dimension as string;
        if (!acc[dim]) acc[dim] = [];
        acc[dim].push(value.text as string);
        return acc;
      }, {} as Record<string, string[]>);
      /* eslint-enable no-param-reassign */

      return (
        <div>
          <div style={{marginBottom: '7px'}}>
            <span style={{fontWeight: 'bold'}}>{dimensionLabels.DRP_TYPE}: </span>
            <span>{grouped.DRP_TYPE?.map((v) => tEnum('DrpType', v)).join(',') || '-'}</span>
          </div>
          <div style={{marginBottom: '7px'}}>
            <span style={{fontWeight: 'bold'}}>{dimensionLabels.DRP_CAUSE}: </span>
            <span>{grouped.DRP_CAUSE?.map((v) => tEnum('DrpCause', v)).join(',') || '-'}</span>
          </div>
          <div>
            <span style={{fontWeight: 'bold'}}>{dimensionLabels.DRP_INTERVENTION}: </span>
            <span>{grouped.DRP_INTERVENTION?.map((v) => tEnum('DrpPlannedIntervention', v)).join(',') || '-'}</span>
          </div>
        </div>
      );
    },
    [t, tEnum]
  );

  const findShortcutIconName = useCallback(
    (instanceId: number | undefined) => (shortcuts || []).find((shortcut) => shortcut.combinationInstanceId === instanceId)?.iconName,
    [shortcuts]
  );

  const [confirmDeleteAnchor, setConfirmDeleteAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flex: 1, minHeight: 0}}>
        <Typography variant='labelBold' style={{fontSize: 16}}>
          {t('hospitalization.detail.phRec.combination.list.title')}
        </Typography>
        <DlMuiButton
          style={{height: 30, width: 140}}
          type='chip-filled'
          title={t('hospitalization.detail.phRec.combination.create.new')}
          onClick={onNewCombination}
        />
      </div>

      {isLoading ? (
        <LoadingSpinner isVisible />
      ) : (
        <LightTable
          data={instances}
          isLoading={isLoading}
          columns={[
            {
              field: 'label',
              width: 150,
              title: t('hospitalization.detail.phRec.combination.label'),
              render: (row) => t(row.label || '-'),
            },
            {
              field: 'icon',
              width: 100,
              title: t('hospitalization.detail.phRec.combination.icon'),
              render: (row) => {
                const iconName = findShortcutIconName(row.id);
                return iconName ? (
                  <DlMuiDynamicIcon
                    iconName={iconName}
                    noButtonStyles
                    style={{pointerEvents: 'none', justifyContent: 'flex-start'}}
                    svgIconStyle={{width: '40%', height: '40%', color: 'var(--dlLightIconButtonFill)'}}
                    sx={{p: 0, m: 0}}
                  />
                ) : (
                  <span />
                );
              },
            },
            {
              field: 'values',
              title: t('hospitalization.detail.phRec.combination.values'),
              minWidth: 150, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the column
              render: (row) => <Typography variant='value'>{getValuesPreview(row)}</Typography>,
            },
            {
              field: 'actions',
              title: '',
              width: 140,
              render: (row) => (
                <div style={{display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-end', width: '100%'}}>
                  {row.ownerId && (
                    <>
                      <DlMuiButton
                        style={{height: 26, minWidth: 26, padding: '0 8px', width: '100%'}}
                        type='chip'
                        title={t('common.edit')}
                        onClick={() => onEdit(row)}
                      />
                      <PopupWrapper
                        openButton={
                          <DlMuiButton
                            style={{height: 26, minWidth: 26, padding: '0 8px', width: '100%'}}
                            type='chip-red'
                            title={t('common.confirmDialog.delete')}
                            onClick={(e) => setConfirmDeleteAnchor(e.target)}
                          />
                        }
                        anchorTag={confirmDeleteAnchor}
                        direction='down'
                        yOffset={4}
                        showKeepDeleteButtons
                        cancelTitle='global.no'
                        confirmTitle='global.yes'
                        onClose={() => {
                          setConfirmDeleteAnchor(null);
                        }}
                        onConfirm={() => {
                          setConfirmDeleteAnchor(null);
                          onDelete(row.id);
                        }}
                      >
                        <Typography variant='sectionHeader'>{t('hospitalization.detail.phRec.combination.confirm.delete')}</Typography>
                      </PopupWrapper>
                    </>
                  )}
                </div>
              ),
            },
          ]}
          style={{overflowY: 'auto'}}
        />
      )}
    </>
  );
};
